import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Field, change, reduxForm } from 'redux-form';

import { showFailure } from 'actions/notificationActions';
import { deleteCotenant, deleteAdditionalOccupant } from 'actions/landlordTenantsActions';

import OutlinedSelect from 'helpers/reduxFormMaterialUiNext/OutlinedSelect';
import TextField from 'helpers/reduxFormMaterialUiNext/TextField';
import MenuItem from 'material-ui/MenuItem';

import * as formatter from 'helpers/Formatter';
import * as validator from 'helpers/Validator';
import { fetchCurrentLeasesForSingleUnits } from 'helpers/PropertiesHelper';

import CurrentLeasesOfUnit from 'components/shared/CurrentLeasesOfUnit';
import RenderDropzoneMultiFileInput from 'components/shared/RenderDropzoneMultiFileInput';
import PropertyUnitSelector from 'components/shared/PropertyUnitSelector';
import Header from 'components/shared/Header';
import Pets from 'components/landlord_app/applications/ManualApplicantDrawer/Pets';
import EmergencyContacts from 'components/landlord_app/applications/ManualApplicantDrawer/EmergencyContacts';
import Vehicles from 'components/landlord_app/applications/ManualApplicantDrawer/Vehicles';

import LeaseHoldersFieldArray from 'components/landlord_app/rent_roll/LeaseOverview/LeaseHoldersFieldArray';
import AdditionalOccupantsFieldArray from 'components/landlord_app/rent_roll/LeaseOverview/AdditionalOccupantsFieldArray';
import { CardText } from 'material-ui/Card';
import Card from '@material-ui/core/Card';

import LeaseItems from 'components/shared/LeaseItems/LeaseItems';

import { BooleanRadioButtons } from 'helpers/BooleanRadioButtons';
import { getProperty } from 'actions/landlordPropertiesActions';
import { getTranslatedString } from 'helpers/i18n';

const validate = values => {
  const errors = {};
  if (!values.property) errors.property = 'Required';
  if (!values.unit_selected) errors.unit_selected = 'Required';
  if (!values.lease_payment_day) errors.lease_payment_day = 'Required';
  if (!values.cotenants || !values.cotenants.length) {
    errors.cotenants = {
      _error:
        'You must add at least one co-applicant. If you are applying by yourself, select no above.',
    };
  } else {
    const cotenantsArrayErrors = [];
    values.cotenants.forEach((cotenant, cotenantIndex) => {
      const cotenantErrors = {};
      if (!cotenant || cotenant.is_guarantor === undefined) {
        cotenantErrors.is_guarantor = 'Required';
        cotenantsArrayErrors[cotenantIndex] = cotenantErrors;
      }
    });
    if (cotenantsArrayErrors.length) errors.cotenants = cotenantsArrayErrors;
  }

  return errors;
};

const style = {
  table_header: {
    margin: '30px 0px 10px 0px',
    backgroundColor: '#f1f1f1',
  },
  transactionCard: {
    color: '#424242 !important',
    fontFamily: '"Circular", sans-serif !important',
    height: '100%',
    overflow: 'hidden',
    borderRadius: '8px',
    marginBottom: '15px',
  },
};

let ManualLeaseCreatorForm = props => {
  const onUnitChange = event => {
    if (!event.value) return;

    props.getCurrentLeasesOfUnit(event.value);

    // TODO: currently, when using for_multiselect, it does not retrieve monthly_rent.
    // We need to get monthly_rent by fetching the unit on a separate call.
    if (event.value[2]) {
      // event.value[2]: desired unit price
      props.changeFieldValue('monthly_rent', formatter.onlyNumbers(event.value[2]));
    }
  };

  const leasePaymentDayOptions = Array.from(Array(28).keys()).map((day_number, i) => (
    <MenuItem value={day_number + 1} key={day_number + 1}>
      {day_number + 1}
    </MenuItem>
  ));

  const doubleUnitBookCheck = (value, fields) => {
    return validator.doubleUnitBook(value, fields, props.currentLeasesOfUnit);
  };

  const getUnits = value => {
    const property_id = value && value.value;
    if (property_id) {
      props.getProperty(property_id);
      props.clearCurrentLeasesOfUnit();
      props.changeFieldValue('unit_selected', null);

      props.dispatch(props.getUnits(property_id, { for_multiselect: true })).then(response => {
        fetchCurrentLeasesForSingleUnits(
          response.value.data,
          props.getCurrentLeasesOfUnit,
          props.changeFieldValue
        );
      });
    }
  };

  return (
    <div>
      <h3 style={{ textAlign: 'center' }}> Manually Add Lease </h3>
      <p style={{ textAlign: 'justify', fontSize: '14px' }}>
        Manually upload a lease with an existing tenant. This should only be done if your company is
        new to yuhu with existing tenants. For prospective tenants, leases are auto-generated once a
        tenant applies to rent, so there&rsquo;s no need to upload leases for prospective tenants or
        renewals.
      </p>

      <Card style={style.transactionCard}>
        <CardText style={{ padding: '0' }}>
          <LeaseHoldersFieldArray
            deleteCotenant={props.deleteCotenant}
            showFailure={props.showFailure}
          />
        </CardText>
      </Card>

      <Card style={style.transactionCard}>
        <CardText style={{ padding: '0' }}>
          <AdditionalOccupantsFieldArray
            deleteAdditionalOccupant={props.deleteAdditionalOccupant}
            showFailure={props.showFailure}
          />
        </CardText>
      </Card>

      <Card style={style.transactionCard}>
        <CardText style={{ padding: '0 0px 15px' }}>
          <Header name="PETS" />
          <div className="row" style={{ padding: '0 30px' }}>
            <Pets {...props} newMUI />
          </div>
        </CardText>
      </Card>

      <Card style={style.transactionCard}>
        <CardText style={{ padding: '0 0px 15px' }}>
          <Header
            name={getTranslatedString('landlordPortal.manualLeaseCreatorForm.vehiclesUpcase')}
          />
          <div className="row" style={{ padding: '0 30px' }}>
            <Vehicles {...props} outlined />
          </div>
        </CardText>
      </Card>

      <Card style={style.transactionCard}>
        <CardText style={{ padding: '0 0px 15px' }}>
          <Header name="EMERGENCY CONTACTS" />
          <div className="row" style={{ padding: '0 30px' }}>
            <EmergencyContacts {...props} newMUI />
          </div>
        </CardText>
      </Card>

      <Card style={style.transactionCard}>
        <CardText style={{ padding: '0' }}>
          <Header name="LEASE DETAILS" />
          <div className="row" style={{ padding: '15px 30px' }}>
            <PropertyUnitSelector
              properties={props.properties}
              units={props.units}
              onPropertyChange={getUnits}
              propertyInnerDivStyle={{
                width: '100%',
                fontSize: '14px',
                fontWeight: '100 !important',
              }}
              propertyMenuStyle={{ minWidth: '250px', fontSize: '14px' }}
              propertyStyle={{ textAlign: 'left', fontSize: '14px' }}
              unitMenuStyle={{ fontSize: '14px' }}
              unitStyle={{ textAlign: 'left', fontSize: '14px' }}
              onUnitChange={onUnitChange}
              variant="outlined"
            />

            <div className="row">
              <div className="col-sm-6" style={{ marginTop: '15px' }}>
                <Field
                  name="lease_start_date"
                  component={TextField}
                  type="date"
                  label="Start Lease Date"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  validate={[validator.required, validator.dateLeaseRange, doubleUnitBookCheck]}
                />
              </div>
              <div className="col-sm-6" style={{ marginTop: '15px' }}>
                <Field
                  name="lease_end_date"
                  component={TextField}
                  type="date"
                  label="End Lease Date"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  validate={[validator.required, validator.dateLeaseRange]}
                />
              </div>
            </div>

            <CurrentLeasesOfUnit leases={props.currentLeasesOfUnit} />

            <div className="row radiobutton-label" style={{ marginTop: '15px' }}>
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <h4 style={{ fontWeight: 'bold' }}>Is the tenant on a month-to-month lease?</h4>
              </div>
              <div style={{ textAlign: 'center', fontSize: '14px' }}>
                If your tenant(s) are on a month-to-month lease, add one year to the date you most
                recently increased their rent and use this date for the lease end date field above.
              </div>
              <div className="col-sm-12" style={{ textAlign: 'center', marginTop: '15px' }}>
                <BooleanRadioButtons
                  name="month_to_month"
                  style={{ width: '187px', margin: 'auto' }}
                />
              </div>
            </div>
          </div>
        </CardText>
      </Card>

      <Card style={style.transactionCard}>
        <CardText style={{ padding: '0' }}>
          <Header name="LEASE PRICING" />
          <div className="row" style={{ padding: '15px 30px' }}>
            <div className="col-sm-4" style={{ textAlign: 'center', marginTop: '15px' }}>
              <Field
                name="monthly_rent"
                type="text"
                component={TextField}
                label="Monthly Rent"
                validate={[validator.required]}
                variant="outlined"
                {...formatter.currencyMask}
              />
            </div>
            <div className="col-sm-4" style={{ textAlign: 'center', marginTop: '15px' }}>
              <Field
                name="deposit_amount"
                type="text"
                component={TextField}
                label="Rental Deposit"
                validate={[validator.required]}
                variant="outlined"
                {...formatter.currencyMask}
              />
            </div>
            <div
              className="col-sm-4"
              style={{ textAlign: 'center', marginTop: '15px', padding: '0 30px' }}
            >
              <Field
                name="key_deposit"
                type="text"
                component={TextField}
                label="Key Deposit"
                variant="outlined"
                {...formatter.currencyMask}
              />
            </div>
          </div>
          <div className="row" style={{ marginBottom: '15px', textAlign: 'center' }}>
            <div
              className="col-sm-4 col-sm-offset-4"
              style={{ textAlign: 'center', marginTop: '15px' }}
            >
              <Field
                name="lease_payment_day"
                component={OutlinedSelect}
                label="Lease Payment Day"
                parse={formatter.onlyNumbers}
                validate={[validator.required]}
                fullWidth
              >
                {leasePaymentDayOptions}
              </Field>
            </div>
          </div>
        </CardText>
      </Card>

      <Card style={style.transactionCard}>
        <CardText style={{ padding: '0' }}>
          <Header name="Ancillary Items" />
          <div className="row" style={{ padding: '0 30px' }}>
            <LeaseItems formName={props.name} />
          </div>
        </CardText>
      </Card>

      <Card style={style.transactionCard}>
        <CardText style={{ padding: '0' }}>
          <Header name="UPLOAD LEASE" />
          <div className="row" style={{ padding: '15px 30px', textAlign: 'center' }}>
            <div className="col-sm-12">
              <Field
                name="lease_file"
                component={RenderDropzoneMultiFileInput}
                multiple={false}
                acceptedFileTypes="image/jpeg, image/png, image/jpg, application/pdf"
                label="Click or drag your lease file here."
              />
            </div>
          </div>
        </CardText>
      </Card>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    form: ownProps.name,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    changeFieldValue: (field, value) => {
      dispatch(change('manual_lease_creator_form', field, value));
    },
    removeFieldValue: field => {
      dispatch(change('manual_lease_creator_form', field, ' '));
    },
    showFailure,
    deleteCotenant,
    deleteAdditionalOccupant,
    getProperty: propertyId => {
      dispatch(getProperty(propertyId));
    },
  };
};

ManualLeaseCreatorForm = compose(
  connect(mapStateToProps, mapDispatchToProps),
  reduxForm({
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
    validate,
    initialValues: {
      month_to_month: false,
      lease_payment_day: 1,
    },
  })
)(ManualLeaseCreatorForm);

export default ManualLeaseCreatorForm;
